// components/ui/InfoBulle.jsx
// ============================================================================
// INFOBULLE KLASYS — composant partagé (DESIGN-SYSTEM.md, « Infobulles »)
// ============================================================================
// Carte blanche qui s'ouvre au survol (ou au focus clavier) d'une petite icône :
// icône de ton + titre + contenu libre, avec une flèche vers le déclencheur.
//
// Rendue dans un portail (Radix Tooltip) : elle n'est jamais coupée par un
// parent en `overflow-hidden` (accordéons, cartes) et se replace seule quand
// elle manque de place.
//
//   <InfoBulle titre="Statistiques globales uniquement" ton="attention">
//     Cette navigation n'a aucun impact sur les paiements.
//   </InfoBulle>
//
// Props
//   titre              titre en gras (facultatif)
//   children           contenu (texte ou liste)
//   ton                'info' (bleu KLASYS, défaut) | 'attention' (jaune)
//   cote / alignement  position : 'top' | 'right' | 'bottom' | 'left' / 'start' | 'center' | 'end'
//   largeur            classe de largeur de la carte (défaut `w-80`)
//   iconeClassName     couleur de l'icône déclencheur, ex. `text-yellow-300` sur fond bleu
//   tailleIcone        taille de l'icône déclencheur (défaut `h-4 w-4`)
//   declencheur        élément qui remplace l'icône par défaut
//   libelle            texte lu par les lecteurs d'écran sur le déclencheur
// ============================================================================

import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { AlertCircle, Info } from 'lucide-react';

const TONS = {
  info: {
    Icone: Info,
    bordure: 'border-[#246990]/20',
    fleche: 'fill-white',
    icone: 'text-[#246990]',
    titre: 'text-[#246990]'
  },
  attention: {
    Icone: AlertCircle,
    bordure: 'border-yellow-200',
    fleche: 'fill-white',
    icone: 'text-yellow-500',
    titre: 'text-yellow-700'
  }
};

const InfoBulle = ({
  titre,
  children,
  ton = 'info',
  cote = 'bottom',
  alignement = 'end',
  largeur = 'w-80',
  iconeClassName,
  tailleIcone = 'h-4 w-4',
  declencheur,
  libelle
}) => {
  const style = TONS[ton] || TONS.info;
  const { Icone } = style;

  return (
    <TooltipPrimitive.Provider delayDuration={150} skipDelayDuration={100}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          {declencheur || (
            // Un span, pas un bouton : l'infobulle se place souvent dans un
            // bouton (en-tête d'accordéon), où un bouton imbriqué est invalide.
            <span
              tabIndex={0}
              aria-label={libelle || titre || 'Informations'}
              className="inline-flex cursor-help items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40"
              onClick={(e) => e.stopPropagation()}
            >
              <Icone className={`${tailleIcone} ${iconeClassName || style.icone}`} />
            </span>
          )}
        </TooltipPrimitive.Trigger>

        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={cote}
            align={alignement}
            sideOffset={6}
            collisionPadding={12}
            className={`z-[1000] ${largeur} max-w-[calc(100vw-24px)] rounded-lg border ${style.bordure} bg-white p-3 text-left text-xs text-gray-800 shadow-xl`}
          >
            <div className="flex items-start gap-2">
              <Icone className={`mt-0.5 h-4 w-4 flex-shrink-0 ${style.icone}`} />
              <div className="min-w-0 flex-1">
                {titre && <p className={`mb-1 font-semibold ${style.titre}`}>{titre}</p>}
                <div className="leading-relaxed text-gray-600">{children}</div>
              </div>
            </div>
            <TooltipPrimitive.Arrow width={12} height={6} className={style.fleche} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

export default InfoBulle;
